"use client";

import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import {
  TextQuestion,
  TextInputType,
  TextField,
  TextFieldValidationType,
} from "@/commonTypes/question";
import FloatingLabelInput from "@/components/FloatingLabelInput/FloatingLabelInput";
import { getInputAttributes } from "@/utils/inputAttributes";
import { useFieldValidation } from "@/hooks/useFieldValidation";
import { useNeverBounce } from "@/hooks/useNeverBounce";
import {
  INTEGER_VALIDATION_TYPES,
  DECIMAL_VALIDATION_TYPES,
} from "@/validation/textFieldSchemas";
import SegmentedControl from "@/components/SegmentedControl/SegmentedControl";
import {
  IMPERIAL_TYPES,
  METRIC_TYPES,
  autoConvertUnits,
  cmToImperialHeight,
  kgToLbs,
} from "@/utils/unitConversion";
import { UnitSystem, UNIT_PREFERENCE_KEY } from "@/constants/surveyMetadata";

export type { UnitSystem } from "@/constants/surveyMetadata";
export { UNIT_PREFERENCE_KEY } from "@/constants/surveyMetadata";

/**
 * Filters and sanitizes input based on the field's validation type.
 * - Text types: no filtering
 * - Integer types: only digits allowed
 * - Decimal types: digits and at most one decimal point
 * Returns the sanitized value.
 */
function sanitizeNumericInput(
  value: string,
  validationType: TextFieldValidationType,
): string {
  if (INTEGER_VALIDATION_TYPES.has(validationType)) {
    // Only allow digits
    return value.replace(/[^0-9]/g, "");
  }

  if (DECIMAL_VALIDATION_TYPES.has(validationType)) {
    // Allow digits and at most one decimal point
    let sanitized = value.replace(/[^0-9.]/g, "");

    // Ensure only one decimal point
    const parts = sanitized.split(".");
    if (parts.length > 2) {
      sanitized = parts[0] + "." + parts.slice(1).join("");
    }

    return sanitized;
  }

  // Other types: no filtering
  return value;
}

export interface TextInputProps {
  question: TextQuestion;
  values: Record<string, string>;
  onChange: (questionId: string, answers: Record<string, string>) => void;
  onValidationChange?: (questionId: string, isValid: boolean) => void;
  unitPreferenceMap?: Record<string, UnitSystem>;
  onUnitPreferenceChange?: (key: string, unit: UnitSystem) => void;
  disabled?: boolean;
  infoToast?: ReactNode;
}

export default function TextInput({
  question,
  values,
  onChange,
  onValidationChange,
  unitPreferenceMap,
  onUnitPreferenceChange,
  disabled = false,
  infoToast,
}: TextInputProps) {
  const t = useTranslations();
  const storedUnit = question.unitSwitcher ? unitPreferenceMap?.[question.unitSwitcher] : undefined;
  const initialUnit: UnitSystem = storedUnit === "imperial" || storedUnit === "metric" ? storedUnit : "imperial";
  const [unitPreference, setUnitPreference] = useState<UnitSystem>(initialUnit);

  // Preserves the user's exact lbs input to avoid floating point artifacts on round-trip conversion.
  // (e.g., 200 lbs → 90.7 kg → 199.96 lbs — we show "200" instead)
  const [rawImperialInput, setRawImperialInput] = useState<string | null>(null);

  const {
    questionId,
    questionTranslationKey,
    answers,
    bodyTextTranslationKey,
    unitSwitcher,
  } = question;
  const questionText = t(questionTranslationKey);
  const bodyText = bodyTextTranslationKey ? t(bodyTextTranslationKey) : null;

  const isEmailQuestion = answers.some((a) => a.inputType === TextInputType.EMAIL);

  // Free-text fields (name, mobile, email) contain PII and must be masked in Datadog RUM.
  const isFreeTextField = answers.some(
    (a) =>
      a.inputType === TextInputType.TEXT ||
      a.inputType === TextInputType.EMAIL ||
      a.inputType === TextInputType.PHONE,
  );

  // NeverBounce controls validation when active; otherwise fall back to regex/field validation.
  const nb = useNeverBounce(isEmailQuestion, t("validation.email-soft-reject"));
  const nbControlsValidation = isEmailQuestion && nb.active;

  // useLayoutEffect fires before paint — prevents any frame where Next could flash
  useLayoutEffect(() => {
    if (nbControlsValidation) {
      onValidationChange?.(questionId, nb.accepted);
    }
  }, [nbControlsValidation, nb.accepted, questionId, onValidationChange]);

  const needsUnitSwitcher = !!unitSwitcher;
  const isHeightGroup = unitSwitcher === "height";

  // Persist the default preference on mount so it's always explicitly recorded,
  // even if the user never toggles away from the default.
  useEffect(() => {
    if (needsUnitSwitcher && onUnitPreferenceChange && unitSwitcher && !unitPreferenceMap?.[unitSwitcher]) {
      onUnitPreferenceChange(unitSwitcher, initialUnit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only on mount
  }, []);

  const activeFields = useMemo(() => {
    if (!needsUnitSwitcher) return answers;
    const activeSet =
      unitPreference === "imperial" ? IMPERIAL_TYPES : METRIC_TYPES;
    return answers.filter((field) => activeSet.has(field.validationType));
  }, [needsUnitSwitcher, unitPreference, answers]);

  // Context stores a single canonical metric value keyed by unitSwitcher ("height" or "weight").
  // This memo derives the display values for whichever unit system the user has toggled to.
  //  - No switcher: pass through values unchanged.
  //  - Metric active: pass through the stored raw value to preserve user input (e.g., "170.").
  //  - Imperial active: use rawImperialInput if set, otherwise convert from metric.
  const displayValues = useMemo(() => {
    if (!needsUnitSwitcher) return values;

    const storedRaw = values[unitSwitcher!] ?? "";
    const stored = parseFloat(storedRaw);
    const derived: Record<string, string> = {};

    if (unitPreference === "metric") {
      // Metric is active — preserve the raw stored value to allow typing decimals
      for (const field of activeFields) {
        derived[field.id] = storedRaw;
      }
    } else if (isHeightGroup) {
      // Imperial height, convert stored cm to feet + inches
      if (!isNaN(stored) && stored > 0) {
        const { ft, inches } = cmToImperialHeight(stored);
        const ftField = answers.find(
          (field) => field.validationType === "height_ft",
        );
        const inField = answers.find(
          (field) => field.validationType === "height_in",
        );
        if (ftField) derived[ftField.id] = String(ft);
        if (inField) derived[inField.id] = String(inches);
      }
    } else if (unitSwitcher === "weight") {
      const lbsField = answers.find(
        (field) => field.validationType === "weight_lbs",
      );
      if (lbsField) {
        if (rawImperialInput !== null) {
          // Show user's exact input to avoid floating point artifacts
          derived[lbsField.id] = rawImperialInput;
        } else if (!isNaN(stored) && stored > 0) {
          // Fallback: convert from kg (used when loading saved data or switching units)
          derived[lbsField.id] = String(Math.round(kgToLbs(stored)));
        }
      }
    } else {
      throw new Error(`Unknown unitSwitcher value: "${unitSwitcher}"`);
    }
    return derived;
  }, [
    values,
    needsUnitSwitcher,
    unitPreference,
    unitSwitcher,
    activeFields,
    answers,
    isHeightGroup,
    rawImperialInput,
  ]);

  // For email questions only, NeverBounce controls validation state when active
  const { inputErrors } = useFieldValidation(
    activeFields,
    displayValues,
    questionId,
    nbControlsValidation ? undefined : onValidationChange,
  );

  // Normalize stored value to remove trailing decimals (e.g., "180." → "180")
  const normalizeStoredValue = useCallback(() => {
    if (!unitSwitcher) return;
    const storedRaw = values[unitSwitcher] ?? "";
    const parsed = parseFloat(storedRaw);
    const normalized = isNaN(parsed) ? "" : String(parsed);
    if (normalized !== storedRaw) {
      onChange(questionId, { [unitSwitcher]: normalized });
    }
  }, [unitSwitcher, values, onChange, questionId]);

  // Toggle flips local state, persists the preference to the parent, and normalizes stored value.
  const handleUnitChange = useCallback(
    (newUnit: string) => {
      const unit = newUnit as UnitSystem;
      setUnitPreference(unit);
      if (onUnitPreferenceChange && unitSwitcher) {
        onUnitPreferenceChange(unitSwitcher, unit);
      }
      normalizeStoredValue();
    },
    [onUnitPreferenceChange, unitSwitcher, normalizeStoredValue],
  );

  // Normalize values on blur (e.g., "155." → "155")
  const handleFieldBlur = useCallback(() => {
    // Normalize rawImperialInput but keep it
    setRawImperialInput((prev) => {
      if (prev === null) return null;
      const parsed = parseFloat(prev);
      return isNaN(parsed) ? null : String(parsed);
    });
    normalizeStoredValue();
  }, [normalizeStoredValue]);

  // Always convert to metric and emit as a single canonical key ("height" or "weight").
  const handleFieldChange = useCallback(
    (field: TextField, rawValue: string) => {
      // Sanitize input: filter invalid characters based on validation type
      const sanitizedValue = sanitizeNumericInput(
        rawValue,
        field.validationType,
      );

      if (!needsUnitSwitcher) {
        const newAnswers: Record<string, string> = {};
        for (const activeField of activeFields) {
          newAnswers[activeField.id] =
            activeField.id === field.id
              ? sanitizedValue
              : (values[activeField.id] ?? "");
        }
        onChange(questionId, newAnswers);
        return;
      }

      // Track user's lbs input to preserve it on display
      if (field.validationType === "weight_lbs") {
        setRawImperialInput(sanitizedValue);
      } else if (field.validationType === "weight_kg") {
        // Clear when user edits in metric mode
        setRawImperialInput(null);
      }

      // Build all field values (both systems) using current display + typed value
      const allValues: Record<string, string> = {};
      for (const answerField of answers) {
        allValues[answerField.id] =
          answerField.id === field.id
            ? sanitizedValue
            : (displayValues[answerField.id] ?? "");
      }
      autoConvertUnits(field, allValues, answers);

      // Extract the canonical metric value
      const metricType = isHeightGroup
        ? "height_cm"
        : unitSwitcher === "weight"
          ? "weight_kg"
          : null;
      if (!metricType)
        throw new Error(`Unknown unitSwitcher value: "${unitSwitcher}"`);
      const metricField = answers.find(
        (field) => field.validationType === metricType,
      );
      onChange(questionId, {
        [unitSwitcher!]: allValues[metricField?.id ?? ""] ?? "",
      });
    },
    [
      needsUnitSwitcher,
      activeFields,
      answers,
      values,
      displayValues,
      onChange,
      questionId,
      isHeightGroup,
      unitSwitcher,
    ],
  );

  const isSingleField = activeFields.length === 1;
  const containerClass = isSingleField
    ? "text-input-wrapper"
    : "text-group-container";

  return (
    <div
      className={`question-container${needsUnitSwitcher ? " has-unit-switcher" : ""}`}
    >
      <h1 className="question-title">{questionText}</h1>
      {bodyText && <p className="question-subtitle">{bodyText}</p>}

      {needsUnitSwitcher && (
        <div className="unit-switcher-container">
          <SegmentedControl
            options={
              isHeightGroup
                ? [
                    { value: "imperial", label: t("units.height.ft") },
                    { value: "metric", label: t("units.height.cm") },
                  ]
                : [
                    { value: "imperial", label: t("units.weight.lbs") },
                    { value: "metric", label: t("units.weight.kg") },
                  ]
            }
            value={unitPreference}
            onChange={handleUnitChange}
            ariaLabel={t("aria.unit-system")}
            disabled={disabled}
          />
        </div>
      )}

      {/* NeverBounce requires inputs to be inside a <form>. noValidate suppresses
          browser-native validation since Zod handles that instead. */}
      <form noValidate onSubmit={(e) => e.preventDefault()} className={containerClass} data-dd-privacy={isFreeTextField ? "mask" : undefined}>
        {activeFields.map((field) => {
          const fieldInputType = field.inputType || TextInputType.TEXT;
          const { htmlType, inputMode, autoComplete } = getInputAttributes(
            fieldInputType,
            field.validationType,
          );
          const validation = inputErrors[field.id];
          const fieldValue = displayValues[field.id] || "";
          const isValid = nbControlsValidation
            ? nb.accepted
            : fieldValue.length > 0 && !validation?.error;
          const showError = nbControlsValidation
            ? !!nb.error
            : (validation?.error ?? false);
          const errorMsg = nbControlsValidation
            ? nb.error
            : validation?.errorMessage;
          return (
            <div
              key={field.id}
              className={isSingleField ? undefined : "text-group-field"}
            >
              <FloatingLabelInput
                label={t(field.translationKey)}
                value={fieldValue}
                onChange={(value) => handleFieldChange(field, value)}
                onBlur={handleFieldBlur}
                inputType={htmlType ?? fieldInputType}
                inputMode={inputMode}
                autoComplete={autoComplete}
                disabled={disabled}
                error={showError}
                errorMessage={errorMsg}
                valid={isValid}
              />
            </div>
          );
        })}
      </form>

      {infoToast && <div className="info-toast-container">{infoToast}</div>}
    </div>
  );
}
